import React, { Suspense, lazy } from 'react';
import { Box, Skeleton, CircularProgress, Typography } from '@mui/material';

// Lazy load chart components
const LazyRevenueChart = lazy(() => 
  import('./RevenueChart').then(module => ({ default: module.RevenueChart }))
);

const LazyExpenseChart = lazy(() => 
  import('./ExpenseChart').then(module => ({ default: module.ExpenseChart }))
);

const LazyProfitLossChart = lazy(() => 
  import('./ProfitLossChart').then(module => ({ default: module.ProfitLossChart }))
);

// Chart loading fallback component
const ChartLoadingFallback: React.FC<{ height?: number }> = ({ height = 400 }) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      height,
      gap: 2
    }}
  >
    <CircularProgress size={40} />
    <Typography variant="body2" color="text.secondary">
      Loading chart...
    </Typography>
  </Box>
);

// Chart error fallback component
const ChartErrorFallback: React.FC<{ height?: number; onRetry?: () => void }> = ({ 
  height = 400, 
  onRetry 
}) => (
  <Box 
    sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center', 
      height,
      gap: 2
    }}
  >
    <Typography variant="body2" color="error">
      Failed to load chart
    </Typography>
    {onRetry && (
      <button onClick={onRetry} style={{ padding: '8px 16px', cursor: 'pointer' }}>
        Retry
      </button>
    )}
  </Box>
);

// Error boundary for charts
class ChartErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback?: React.ComponentType<any> },
  { hasError: boolean }
> {
  constructor(props: any) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Chart error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || ChartErrorFallback;
      return <FallbackComponent onRetry={() => this.setState({ hasError: false })} />;
    }

    return this.props.children;
  }
}

// Lazy chart wrapper components
export const LazyRevenueChartWrapper: React.FC<React.ComponentProps<typeof LazyRevenueChart>> = (props) => (
  <ChartErrorBoundary>
    <Suspense fallback={<ChartLoadingFallback height={props.height} />}>
      <LazyRevenueChart {...props} />
    </Suspense>
  </ChartErrorBoundary>
);

export const LazyExpenseChartWrapper: React.FC<React.ComponentProps<typeof LazyExpenseChart>> = (props) => (
  <ChartErrorBoundary>
    <Suspense fallback={<ChartLoadingFallback height={props.height} />}>
      <LazyExpenseChart {...props} />
    </Suspense>
  </ChartErrorBoundary>
);

export const LazyProfitLossChartWrapper: React.FC<React.ComponentProps<typeof LazyProfitLossChart>> = (props) => (
  <ChartErrorBoundary>
    <Suspense fallback={<ChartLoadingFallback height={props.height} />}>
      <LazyProfitLossChart {...props} />
    </Suspense>
  </ChartErrorBoundary>
);

// Export types for convenience
export type { RevenueChartProps } from './RevenueChart';
export type { ExpenseChartProps } from './ExpenseChart';
export type { ProfitLossChartProps } from './ProfitLossChart';